import { useCallback, useContext, useState } from 'react';
import { useSnackbar } from 'notistack';
import useConfirm from '../../../Components/useConfirm';
import { JobDetailContext } from '../../../Pages/Jobs/context/JobDetailContext';
import { makeApiRequest } from '../../Api';
import { BID_STATUS } from '../constants';
import { resolveAdditionalDepositDue } from '../capabilities';
import { getCheckoutExpireConfirmOptions } from '../messages';
import { bidHasActiveCheckoutLink, statusChangeExpiresCheckoutLink } from '../rules';

const useBidStatusChange = ({ onJobUpdated } = {}) => {
	const { state, dispatch } = useContext(JobDetailContext);
	const { enqueueSnackbar } = useSnackbar();
	const { confirm, confirmDialog } = useConfirm();
	const [depositPrompt, setDepositPrompt] = useState(null);

	const selectedJob = state.selectedJob;
	const bidDetails = selectedJob?.bid_details;

	const promptDepositAmount = useCallback(
		(defaultAmount) =>
			new Promise((resolve) => {
				setDepositPrompt({ defaultAmount, resolve });
			}),
		[],
	);

	const closeDepositPrompt = useCallback(
		(amount) => {
			depositPrompt?.resolve(amount);
			setDepositPrompt(null);
		},
		[depositPrompt],
	);

	const confirmCheckoutExpiry = useCallback(() => {
		const { message, title, confirmText, confirmColor } =
			getCheckoutExpireConfirmOptions('statusChange');
		return confirm(message, { title, confirmText, confirmColor });
	}, [confirm]);

	const patchBidStatus = useCallback(
		async ({ value, confirmExpireCheckout = false, depositPaidAmount }) => {
			const payload = { status: value };
			if (confirmExpireCheckout) {
				payload.confirm_expire_checkout = true;
			}
			if (depositPaidAmount != null) {
				payload.deposit_paid_amount = depositPaidAmount;
			}
			await makeApiRequest('PATCH', `/bids/${bidDetails.id}/`, payload);
		},
		[bidDetails?.id],
	);

	const applyBidStatusChange = useCallback(
		async ({ value, confirmExpireCheckout = false, depositPaidAmount }) => {
			const previousStatus = bidDetails.status;
			const previousDepositPaid = selectedJob.deposit_paid_amount;
			const nextDepositPaid =
				depositPaidAmount != null
					? Number(previousDepositPaid || 0) + Number(depositPaidAmount)
					: previousDepositPaid;

			dispatch({
				type: 'SET_JOB',
				payload: {
					...selectedJob,
					deposit_paid_amount: nextDepositPaid,
					bid_details: {
						...bidDetails,
						status: value,
						active_checkout_link: confirmExpireCheckout
							? false
							: bidDetails.active_checkout_link,
					},
				},
			});
			try {
				await patchBidStatus({
					value,
					confirmExpireCheckout,
					depositPaidAmount,
				});
				if (typeof onJobUpdated === 'function') {
					await onJobUpdated();
				}
			} catch (err) {
				dispatch({
					type: 'SET_JOB',
					payload: {
						...selectedJob,
						deposit_paid_amount: previousDepositPaid,
						bid_details: {
							...bidDetails,
							status: previousStatus,
						},
					},
				});
				const data = err.data || {};
				if (data.requires_checkout_expiry_confirmation) {
					const confirmed = await confirmCheckoutExpiry();
					if (confirmed) {
						await applyBidStatusChange({
							value,
							confirmExpireCheckout: true,
							depositPaidAmount,
						});
					}
					return;
				}
				if (data.requires_deposit_paid_amount) {
					const amount = await promptDepositAmount(
						resolveAdditionalDepositDue(
							bidDetails,
							selectedJob.deposit_paid_amount,
						),
					);
					if (amount != null) {
						await applyBidStatusChange({
							value,
							confirmExpireCheckout,
							depositPaidAmount: amount,
						});
					}
					return;
				}
				enqueueSnackbar(err.message || 'Could not update bid status.', {
					variant: 'error',
				});
			}
		},
		[
			bidDetails,
			selectedJob,
			dispatch,
			patchBidStatus,
			onJobUpdated,
			confirmCheckoutExpiry,
			promptDepositAmount,
			enqueueSnackbar,
		],
	);

	const changeBidStatus = useCallback(
		async (value) => {
			if (!bidDetails || value === bidDetails.status) {
				return;
			}

			let confirmExpireCheckout = false;
			let depositPaidAmount;

			if (
				bidHasActiveCheckoutLink(bidDetails) &&
				statusChangeExpiresCheckoutLink(bidDetails.status, value)
			) {
				const confirmed = await confirmCheckoutExpiry();
				if (!confirmed) return;
				confirmExpireCheckout = true;
			}

			if (
				value === BID_STATUS.DEPOSIT_PAID &&
				bidDetails.status === BID_STATUS.PENDING_DEPOSIT
			) {
				const defaultAmount = resolveAdditionalDepositDue(
					bidDetails,
					selectedJob.deposit_paid_amount,
				);
				const amount = await promptDepositAmount(defaultAmount);
				if (amount == null) return;
				depositPaidAmount = amount;
			}

			await applyBidStatusChange({
				value,
				confirmExpireCheckout,
				depositPaidAmount,
			});
		},
		[
			bidDetails,
			selectedJob.deposit_paid_amount,
			confirmCheckoutExpiry,
			promptDepositAmount,
			applyBidStatusChange,
		],
	);

	return {
		changeBidStatus,
		depositPrompt,
		closeDepositPrompt,
		confirmDialog,
	};
};

export default useBidStatusChange;
